import {
  type GridRenderEditCellParams,
  type GridRowId,
  type GridValidRowModel,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { RiAlertLine } from '@remixicon/react';
import { LOT_DESCRIPTOR, LOT_SELECTABLE_FOR_TRANSFER, type LotDoc } from '@shared/core/search/entities/lot';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Fragment, useEffect, useMemo } from 'react';
import { dateSort, fromUTC, parseDateTime, startOfDay } from 'shared/utils/DateTime';
import UnderlinedMatch from 'src/components/atoms/UnderlinedMatch';
import { AgText } from 'src/components/ui';
import useSystemUnitRounding from 'src/hooks/useSystemUnitRounding';
import { EntitySearchCell } from 'src/modules/entities/search';
import fuzzyComplete from 'src/utils/fuzzyComplete';

type EditLotProduct = {
  readonly commodity?: string | { readonly name: string } | null;
  readonly countryOfOrigin?: string | null;
};

type LotUsage = { readonly slug: string; readonly unitsPicked?: number | null };

type EditLotRowLot = LotUsage & {
  readonly id?: number;
  readonly product?: { readonly commodity?: { readonly name?: string | null } | null } | null;
};

export type EditLotRow = GridValidRowModel & {
  readonly id: GridRowId;
  readonly lot?: EditLotRowLot | null;
  readonly lots?: readonly LotUsage[] | null;
  readonly orderedProduct?: EditLotProduct | null;
  readonly product?: EditLotProduct | null;
  readonly unitsOrdered?: number | null;
  readonly unitsPicked?: number | null;
};

type SelectedLot = {
  readonly grower: string;
  readonly id: number;
  readonly palletTags: LotDoc['palletTags'];
  readonly product: { readonly commodity: { readonly name: string }; readonly description: string };
  readonly quantity: number;
  readonly receiveDate: string;
  readonly slug: string;
  readonly vendor: string;
  readonly warehouseId: number;
};

type LotTotals = Readonly<Record<string, number>>;
type GetLotTotals = (
  productRows: readonly EditLotRow[] | null | undefined,
  targetRow: EditLotRow
) => LotTotals | undefined;

type EditLotCellParams = GridRenderEditCellParams<EditLotRow, SelectedLot | null> & {
  readonly warehouseId: number;
  readonly productsRef?: React.MutableRefObject<readonly EditLotRow[] | null | undefined>;
  readonly clearOnChange?: boolean;
  readonly inputRef?: React.RefObject<HTMLInputElement>;
  readonly getLotTotals?: GetLotTotals;
};

// commodity might be string if the product has been changed
// (this means the product is an algolia result, rather than a processed line item/lot)
const getCommodityFromProduct = (commodity: EditLotProduct['commodity']): string | undefined => {
  return typeof commodity === 'string' ? commodity : commodity?.name;
};

const getCommodityFromLot = (lot: EditLotRowLot | null | undefined): string | null | undefined => {
  return lot?.product?.commodity?.name;
};

/*
  Returns a map of the lots and their totals.
*/
export const defaultGetLotTotals: GetLotTotals = (productRows, targetRow) => {
  return productRows?.reduce<LotTotals>((lotMap, row) => {
    if (row.id === targetRow.id) return lotMap;

    if (row.lots?.length) {
      const lotUnitsTotals = row.lots.reduce<LotTotals>((rowLotTotals, lot) => {
        const currentTotal = rowLotTotals[lot.slug] ?? 0;
        const childRowUnitsPicked = lot.unitsPicked ?? 0;

        return { ...rowLotTotals, [lot.slug]: currentTotal + childRowUnitsPicked };
      }, lotMap);

      return lotUnitsTotals;
    }

    const lotSlug = row.lot?.slug;
    if (!lotSlug) return lotMap;

    const currentTotal = lotMap[lotSlug] ?? 0;
    // `?? 0` is load-bearing: a row carrying neither count would contribute `undefined`, and a
    // NaN lot total makes `unitsRemaining < 0` false — silently suppressing the overdraw warning.
    const unitsRequired = row.unitsPicked ?? row.unitsOrdered ?? 0;

    return { ...lotMap, [lotSlug]: currentTotal + unitsRequired };
  }, {});
};

export default function EditLotCell(params: EditLotCellParams) {
  const apiRef = useGridApiContext();
  useGridSelector(apiRef, (gridApiRef) => gridApiRef.current?.state?.editRows?.[params.id]?.[params.field]);

  const product = params.row.product ?? params.row.orderedProduct;
  // Same `?? 0` contract as defaultGetLotTotals: NaN here silently hides the overdraw warning.
  const unitsForRow = params.row.unitsPicked ?? params.row.unitsOrdered ?? 0;
  const products = params.productsRef?.current;

  const flags = useFlags();
  const getLotTotals = useMemo(() => params.getLotTotals ?? defaultGetLotTotals, [params.getLotTotals]);

  const lotTotals = useMemo(() => {
    return getLotTotals(products, params.row);
  }, [products, params.row, getLotTotals]);

  const productCommodity = getCommodityFromProduct(product?.commodity);
  const lotCommodity = getCommodityFromLot(params.row.lot);

  useEffect(() => {
    if (lotCommodity !== productCommodity && params.clearOnChange) {
      console.debug(
        `Lot commodity (${lotCommodity}) and row commodity (${productCommodity}) are mismatching. Clearing lot.`
      );

      apiRef?.current.setEditCellValue({ id: params.id, field: params.field, value: null });
    }
  }, [lotCommodity, apiRef, productCommodity, params.id, params.clearOnChange, params.field]);

  const filter = useMemo(
    () =>
      LOT_SELECTABLE_FOR_TRANSFER({
        commodity: productCommodity,
        countryOfOrigin: product?.countryOfOrigin,
        warehouseId: params.warehouseId,
      }),
    [productCommodity, product?.countryOfOrigin, params.warehouseId]
  );

  const unitRounding = useSystemUnitRounding();

  return (
    <>
      <EntitySearchCell<LotDoc, SelectedLot>
        {...params}
        descriptor={LOT_DESCRIPTOR}
        filter={filter}
        value={params.value}
        transform={(value) => ({
          id: value.lotId,
          slug: value.slug,
          quantity: unitRounding(value.quantity),
          receiveDate: value.receiveDate,
          vendor: value.vendor,
          grower: value.grower,
          palletTags: value.palletTags,
          warehouseId: value.warehouseId,
          product: { description: value.description, commodity: { name: value.commodity } },
        })}
        getOptionKey={getLotOptionKey}
        isOptionEqualToValue={(option, value) => getLotOptionKey(option) === getLotOptionKey(value)}
        filterOptions={fuzzyComplete(['slug', 'description', 'palletTags.*.tagNumber'], {
          baseSort: dateSort,
          filter: (options: LotDoc[]) => {
            if (flags['disableLotSelectionFiltering']) {
              return options;
            }

            return options.filter((option) => {
              return lotTotals?.[option.slug] === undefined || option.slug === params.value?.slug;
            });
          },
        })}
        textFieldProps={{ sx: { overflow: 'hidden', scrollX: 'auto', wrap: 'nowrap' }, inputRef: params.inputRef }}
        getOptionLabel={(option) => option?.slug ?? ''}
        details={(option, inputValue) => {
          const sourcedFrom = option.vendor ? 'Vendor' : 'Grower';

          const unitsRemaining = option.quantity - (lotTotals?.[option.slug] ?? 0) - unitsForRow;

          const notEnoughUnits = unitsRemaining < 0;
          const isLotMidProductionRun = option.status === 'MID_PRODUCTION_RUN';
          const isLotMidReceiving = option.status === 'MID_RECEIVING';

          const receiveDate = parseDateTime(option.receiveDate);
          const repackDateLabel = getRepackDateLabel(option.repackDate);

          return (
            <>
              <AgText size="14" color="primary">
                <UnderlinedMatch target={option.description} input={inputValue} />
              </AgText>
              <AgText
                size="14"
                color={notEnoughUnits ? 'inherit' : 'secondary'}
                className={notEnoughUnits ? 'flex items-center text-red-text' : 'flex items-center'}
              >
                Available: {option.quantity}, Reserved: {option.reserved}
              </AgText>

              {(isLotMidProductionRun || isLotMidReceiving) && (
                <AgText size="14" color="inherit" className="flex items-center text-red-text">
                  <RiAlertLine size={14} className="inline" /> This lot is mid{' '}
                  {isLotMidProductionRun ? 'production run' : 'receiving'}.
                </AgText>
              )}

              {!isLotMidProductionRun && !isLotMidReceiving && notEnoughUnits && (
                <AgText size="14" color="inherit" className="flex items-center gap-0.5 text-red-text" weight="black">
                  <RiAlertLine size={14} className="inline" />
                  Lot will be overdrawn if picked! ({unitsRemaining})
                </AgText>
              )}

              <AgText size="14" color="secondary">
                Age: {receiveDate ? startOfDay().diff(receiveDate, 'days').days.toFixed() : '--'}
                {option.grade && `, QC Grade: ${option.grade}`}
              </AgText>
              {repackDateLabel && (
                <AgText size="14" color="secondary">
                  Repack Date: {repackDateLabel}
                </AgText>
              )}
              <AgText size="14" color="secondary">
                {sourcedFrom}: {option.vendor || option.grower}
              </AgText>
              <MatchingPalletTags palletTags={option.palletTags} inputValue={inputValue} />
            </>
          );
        }}
        onChange={(e, value) => {
          const nextId = value && typeof value === 'object' && 'id' in value ? value.id : undefined;
          if (nextId === params.value?.id) return;

          apiRef?.current.setEditCellValue({ id: params.id, field: 'palletTagsOnLineItem', value: [] });
        }}
      />
    </>
  );
}

const getLotOptionKey = (option: LotDoc | SelectedLot): number => ('lotId' in option ? option.lotId : option.id);

export const getRepackDateLabel = (repackDate: LotDoc['repackDate']): string | null => {
  if (!repackDate) return null;

  return fromUTC(repackDate)?.toFormat('MM/dd/yyyy') ?? null;
};

type MatchingPalletTag = {
  readonly palletTagId: number;
  readonly parts: ReadonlyArray<{ readonly highlight: boolean; readonly text: string }>;
};

export const getMatchingPalletTagParts = (
  palletTags: LotDoc['palletTags'] | undefined,
  inputValue: string
): MatchingPalletTag[] => {
  return (palletTags ?? []).flatMap((palletTag) => {
    // `insideWords` is read by autosuggest-highlight's match(), never by parse(). On parse() it
    // was inert and matching stayed anchored to `\b`, so typing "234" could not find PT-100234.
    const matches = match(palletTag.tagNumber, inputValue, { insideWords: true });
    const parts = parse(palletTag.tagNumber, matches);

    return parts.some((part) => part.highlight) ? [{ palletTagId: palletTag.palletTagId, parts }] : [];
  });
};

const MatchingPalletTags = ({
  palletTags,
  inputValue,
}: {
  readonly palletTags: LotDoc['palletTags'];
  readonly inputValue: string;
}) => {
  const matchingTags = useMemo(() => getMatchingPalletTagParts(palletTags, inputValue), [palletTags, inputValue]);

  if (!matchingTags.length) return null;

  return (
    <AgText size="14" color="secondary">
      Tags:{' '}
      {matchingTags.map((tag, index) => (
        <Fragment key={tag.palletTagId}>
          {tag.parts.map((part, partIndex) => (
            <span key={partIndex} style={{ textDecoration: part.highlight ? 'underline' : 'inherit' }}>
              {part.text}
            </span>
          ))}
          {index < matchingTags.length - 1 && ', '}
        </Fragment>
      ))}
    </AgText>
  );
};
